import React, { useState, useEffect } from 'react';
import { SignInButton, SignUpButton, Show, UserButton } from '@clerk/react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Upload, Menu, X, ChevronDown, Share2, BookOpen, Server } from 'lucide-react';
import { integrationList } from '@/data/integrations';
import { ThemeToggle } from '@/components/ThemeToggle';

// Preload pages on hover
const preloadPage = (path) => {
  if (path === '/api-docs') {
    import('../pages/ApiDocsPage');
  } else if (path === '/dashboard') {
    import('../pages/DashboardPage');
  } else if (path === '/billing') {
    import('../pages/BillingPage');
  }
};

export default function Header() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileIntegrationsOpen, setMobileIntegrationsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`sticky z-50 w-full transition-all duration-500 ease-in-out ${
        isScrolled
          ? 'top-4 px-4 pointer-events-none border-b-0'
          : 'top-0 border-b border-border'
      }`}
      data-testid="landing-header"
    >
      <div
        className={`mx-auto transition-all duration-500 ease-in-out ${
          isScrolled
            ? `max-w-5xl backdrop-blur-xl bg-card/90 border border-border shadow-elevated ${mobileMenuOpen ? 'rounded-3xl' : 'rounded-full'} px-6 py-2 pointer-events-auto`
            : 'max-w-7xl px-4 sm:px-6 lg:px-8 py-4 backdrop-blur-xl bg-card/95'
        }`}
      >
        <div className="flex justify-between items-center">
          <Link
            to="/"
            className="flex items-center gap-2.5"
          >
            <div className="w-9 h-9 rounded-[10px] bg-gradient-to-br from-brand-from to-brand-to flex items-center justify-center shadow-glow">
              <Upload className="w-4 h-4 text-white" />
            </div>
            <span className="text-[17px] font-extrabold text-foreground tracking-tight whitespace-nowrap">
              Upload to URL
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-2">
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="rounded-full px-5 text-muted-foreground hover:text-brand hover:bg-brand-tint data-[state=open]:text-brand data-[state=open]:bg-brand-tint transition-colors font-medium"
                    data-testid="integrations-dropdown-trigger"
                  >
                    Integrations
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border-border text-popover-foreground p-2 rounded-2xl shadow-card">
                  <DropdownMenuItem
                    onClick={() => navigate('/api-docs')}
                    onMouseEnter={() => preloadPage('/api-docs')}
                    className="hover:bg-accent focus:bg-accent focus:text-brand cursor-pointer py-2.5 rounded-lg group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-tint flex items-center justify-center mr-3 transition-colors">
                      <BookOpen className="w-4 h-4 text-brand" />
                    </div>
                    <span className="font-medium text-foreground">API Docs</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/mcp-server')}
                    onMouseEnter={() => preloadPage('/mcp-server')}
                    className="hover:bg-accent focus:bg-accent focus:text-brand cursor-pointer py-2.5 rounded-lg group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-tint flex items-center justify-center mr-3 transition-colors">
                      <Server className="w-4 h-4 text-brand" />
                    </div>
                    <span className="font-medium text-foreground">MCP Server</span>
                  </DropdownMenuItem>
                  <div className="h-px bg-border my-2 mx-1" />
                  {integrationList.filter(i => i.slug !== 'mcp-server').map((integration) => (
                    <DropdownMenuItem
                      key={integration.slug}
                      onClick={() => navigate(`/${integration.slug}`)}
                      className="hover:bg-accent focus:bg-accent focus:text-foreground cursor-pointer py-2 rounded-lg group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center mr-3 border border-border overflow-hidden transition-all">
                        <img
                          src={integration.logoPath}
                          alt={integration.name}
                          className="w-5 h-5 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden items-center justify-center w-full h-full text-[10px] font-bold text-muted-foreground">
                          {integration.name.substring(0, 2)}
                        </div>
                      </div>
                      <span className="text-sm">{integration.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button
                  variant="ghost"
                  className="rounded-full px-5 text-muted-foreground hover:text-brand hover:bg-brand-tint transition-colors font-medium"
                  data-testid="sign-in-button"
                >
                  Sign In
                </Button>
              </SignInButton>
              <SignUpButton mode="modal">
                <Button
                  className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-6 py-2 font-bold transition-all hover:shadow-glow active:scale-95"
                  data-testid="get-started-button-header"
                >
                  Get Started
                </Button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <Button
                onClick={() => navigate('/dashboard')}
                onMouseEnter={() => preloadPage('/dashboard')}
                className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-6 py-2 font-bold transition-all hover:shadow-glow active:scale-95"
                data-testid="dashboard-button"
              >
                Dashboard
              </Button>
              <UserButton afterSignOutUrl="/" />
            </Show>
            <ThemeToggle />
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            <Show when="signed-out">
              <SignUpButton mode="modal">
                <Button
                  size="sm"
                  className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-4 h-9 text-xs font-bold transition-all hover:shadow-glow active:scale-95"
                  data-testid="get-started-button-header-mobile"
                >
                  Get Started
                </Button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <Button
                onClick={() => navigate('/dashboard')}
                size="sm"
                className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-4 h-9 text-xs font-bold transition-all hover:shadow-glow active:scale-95"
                data-testid="dashboard-button-header-mobile"
              >
                Dashboard
              </Button>
            </Show>
            <ThemeToggle />
            <button
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-border">
            <nav aria-label="Mobile navigation" className="flex flex-col gap-2">
              <button
                onClick={() => setMobileIntegrationsOpen(!mobileIntegrationsOpen)}
                className="flex items-center justify-between rounded-lg px-4 py-3 text-muted-foreground hover:text-brand hover:bg-brand-tint transition-colors w-full"
              >
                <div className="flex items-center gap-3">
                  <Share2 className="w-4 h-4" />
                  <span>Integrations</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileIntegrationsOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileIntegrationsOpen && (
                <div className="pl-11 pr-4 py-2 flex flex-col gap-2">
                  <button
                    onClick={() => {
                      navigate('/api-docs');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 text-muted-foreground hover:text-brand transition-colors flex items-center gap-3"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    API Docs
                  </button>
                  <button
                    onClick={() => {
                      navigate('/mcp-server');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 text-muted-foreground hover:text-brand transition-colors flex items-center gap-3"
                  >
                    <Server className="w-3.5 h-3.5" />
                    MCP Server
                  </button>
                  {integrationList.filter(i => i.slug !== 'mcp-server').map((integration) => (
                    <button
                      key={integration.slug}
                      onClick={() => {
                        navigate(`/${integration.slug}`);
                        setMobileMenuOpen(false);
                      }}
                      className="text-left py-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {integration.name}
                    </button>
                  ))}
                </div>
              )}

              <Show when="signed-out">
                <SignInButton mode="modal">
                  <Button
                    variant="ghost"
                    className="justify-start rounded-lg px-4 py-3 text-muted-foreground hover:text-brand hover:bg-brand-tint w-full transition-colors"
                    data-testid="sign-in-button-mobile"
                  >
                    Sign In
                  </Button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <Button
                    className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-4 py-3 font-bold transition-all hover:shadow-glow w-full"
                    data-testid="get-started-button-mobile"
                  >
                    Get Started
                  </Button>
                </SignUpButton>
              </Show>
              <Show when="signed-in">
                <Button
                  onClick={() => {
                    navigate('/dashboard');
                    setMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="justify-start rounded-lg px-4 py-3 text-muted-foreground hover:text-brand hover:bg-brand-tint w-full transition-colors"
                  data-testid="dashboard-button-mobile"
                >
                  Dashboard
                </Button>
                <div className="flex justify-center py-2">
                  <UserButton afterSignOutUrl="/" />
                </div>
              </Show>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
